import {
  DocumentTextIcon,
  FilmIcon,
  GlobeAltIcon,
  PencilSquareIcon,
  BookOpenIcon,
  ListBulletIcon,
  UserIcon,
  PaintBrushIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { ProjectSettingType, GenerationStepType, ProjectSettingStepStatus } from '@packages/global';
import type { TabType } from './ProjectLayout';
import { useTranslation } from 'react-i18next';

// 导航项类型
export type NavItem =
  | { type: 'setting'; settingType: ProjectSettingType }
  | { type: 'outline' }
  | { type: 'subject' }
  | { type: 'script' }
  | { type: 'episodes' };

// 自动生成状态
export interface AutoGenStatus {
  isActive: boolean;
  currentStep: GenerationStepType | null;
  completedSteps: GenerationStepType[];
  failedStep: GenerationStepType | null;
  totalSteps: number;
  novelFileUrl?: string;
}

interface ProjectSideNavProps {
  activeTab: TabType;
  activeNavItem?: NavItem;
  onItemChange: (item: NavItem) => void;
  autoGenStatus?: AutoGenStatus;
}

// 设定子菜单项配置（不含 label，label 由 i18n 提供）
const settingItemConfigs = [
  { type: 'setting' as const, settingType: ProjectSettingType.STORY_SYNOPSIS, labelKey: 'storySynopsis', icon: BookOpenIcon, genStep: GenerationStepType.STORY_SYNOPSIS },
  { type: 'setting' as const, settingType: ProjectSettingType.WORLDVIEW, labelKey: 'worldview', icon: GlobeAltIcon, genStep: GenerationStepType.WORLDVIEW },
  { type: 'setting' as const, settingType: ProjectSettingType.WRITING_STYLE, labelKey: 'writingStyle', icon: PencilSquareIcon, genStep: GenerationStepType.WRITING_STYLE },
  { type: 'outline' as const, labelKey: 'episodeOutline', icon: ListBulletIcon, genStep: GenerationStepType.EPISODE_OUTLINE },
  { type: 'setting' as const, settingType: ProjectSettingType.ART_STYLE, labelKey: 'artStyle', icon: PaintBrushIcon, genStep: GenerationStepType.ART_STYLE },
  { type: 'subject' as const, labelKey: 'subjectSetting', icon: UserIcon, genStep: GenerationStepType.SUBJECT },
  { type: 'script' as const, labelKey: 'episodeScript', icon: DocumentTextIcon, genStep: GenerationStepType.EPISODE_SCRIPT },
];

// 获取步骤状态
function getStepStatus(
  step: GenerationStepType,
  autoGenStatus?: AutoGenStatus
): ProjectSettingStepStatus {
  if (!autoGenStatus) return ProjectSettingStepStatus.PENDING;

  // 故事梗概步骤：STORY_SYNOPSIS 或 NOVEL_PARSE 有一个完成就显示完成
  if (step === GenerationStepType.STORY_SYNOPSIS) {
    const isNovelParseCompleted = autoGenStatus.completedSteps.includes(GenerationStepType.NOVEL_PARSE);
    const isStorySynopsisCompleted = autoGenStatus.completedSteps.includes(GenerationStepType.STORY_SYNOPSIS);

    if (autoGenStatus.currentStep === GenerationStepType.NOVEL_PARSE) {
      return ProjectSettingStepStatus.IN_PROGRESS;
    }
    if (isNovelParseCompleted || isStorySynopsisCompleted) {
      return ProjectSettingStepStatus.COMPLETED;
    }
  }

  // 分集剧本步骤：EPISODE_SCRIPT 或 NOVEL_SPLIT 有一个完成就显示完成
  if (step === GenerationStepType.EPISODE_SCRIPT) {
    const isNovelSplitCompleted = autoGenStatus.completedSteps.includes(GenerationStepType.NOVEL_SPLIT);
    const isEpisodeScriptCompleted = autoGenStatus.completedSteps.includes(GenerationStepType.EPISODE_SCRIPT);

    if (autoGenStatus.currentStep === GenerationStepType.NOVEL_SPLIT) {
      return ProjectSettingStepStatus.IN_PROGRESS;
    }
    if (isNovelSplitCompleted || isEpisodeScriptCompleted) {
      return ProjectSettingStepStatus.COMPLETED;
    }
  }

  if (autoGenStatus.completedSteps.includes(step)) return ProjectSettingStepStatus.COMPLETED;
  if (autoGenStatus.failedStep === step) return ProjectSettingStepStatus.FAILED;
  if (autoGenStatus.currentStep === step) return ProjectSettingStepStatus.IN_PROGRESS;
  return ProjectSettingStepStatus.PENDING;
}

export default function ProjectSideNav({ activeTab, activeNavItem, onItemChange, autoGenStatus }: ProjectSideNavProps) {
  const { t } = useTranslation('project');

  // 判断菜单项是否高亮
  const isItemActive = (item: typeof settingItemConfigs[0]) => {
    if (item.type === 'script') return activeNavItem?.type === 'script';
    if (item.type === 'outline') return activeNavItem?.type === 'outline';
    if (item.type === 'subject') return activeNavItem?.type === 'subject';
    if (item.type === 'setting' && activeNavItem?.type === 'setting') {
      return item.settingType === activeNavItem.settingType;
    }
    return false;
  };

  const isEpisodesActive = () => activeNavItem?.type === 'episodes';

  return (
    <nav className="flex flex-col gap-0.5">
      {settingItemConfigs.map((item) => {
        const Icon = item.icon;
        const active = isItemActive(item);
        const stepStatus = getStepStatus(item.genStep, autoGenStatus);
        const isGenerating = stepStatus === ProjectSettingStepStatus.IN_PROGRESS;
        const isCompleted = stepStatus === ProjectSettingStepStatus.COMPLETED;
        const isFailed = stepStatus === ProjectSettingStepStatus.FAILED;

        return (
          <button
            key={item.type === 'setting' ? item.settingType : item.type}
            onClick={() => {
              if (item.type === 'setting') {
                onItemChange({ type: 'setting', settingType: item.settingType });
              } else if (item.type === 'outline') {
                onItemChange({ type: 'outline' });
              } else if (item.type === 'subject') {
                onItemChange({ type: 'subject' });
              } else if (item.type === 'script') {
                onItemChange({ type: 'script' });
              }
            }}
            className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-[15px] w-full transition-all cursor-pointer ${active
              ? 'bg-primary/10 text-primary font-medium'
              : 'text-gray-300 hover:bg-bg-elevated hover:text-gray-50'
              }`}
          >
            <Icon className={`w-5 h-5 ${active ? 'text-primary' : 'text-gray-500'}`} />
            <span className="flex-1 text-left">{t(`sideNav.${item.labelKey}`)}</span>
            {autoGenStatus?.isActive && (
              <>
                {isGenerating && (
                  <div className="w-3.5 h-3.5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                )}
                {isCompleted && (
                  <CheckCircleIcon className="w-4 h-4 text-success" />
                )}
                {isFailed && (
                  <XCircleIcon className="w-4 h-4 text-error" />
                )}
              </>
            )}
          </button>
        );
      })}

      {/* 分集视频 */}
      <button
        onClick={() => onItemChange({ type: 'episodes' })}
        className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-[15px] transition-all cursor-pointer ${isEpisodesActive()
          ? 'bg-primary/10 text-primary font-medium'
          : 'text-gray-300 hover:bg-bg-elevated hover:text-gray-50'
          }`}
      >
        <FilmIcon className={`w-5 h-5 ${isEpisodesActive() ? 'text-primary' : 'text-gray-500'}`} />
        <span>{t('sideNav.episodeVideo')}</span>
      </button>
    </nav>
  );
}
